import { SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { useState } from "react";
import { Link } from "wouter";
import { ChevronLeft, ChevronRight, Maximize2, Home, Book } from "lucide-react";

interface SlideData {
  id: number;
  title: string;
  content: JSX.Element;
}

const KeyTakeaway = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-6">
    <div className="flex">
      <div className="flex-shrink-0">
        <div className="w-5 h-5 bg-blue-400 rounded-full flex items-center justify-center">
          <span className="text-xs font-bold text-white">!</span>
        </div>
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-blue-800">Key Takeaway</p>
        <p className="text-sm text-blue-700 mt-1">{children}</p>
      </div>
    </div>
  </div>
);

export default function SupplementsNutraceuticals() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const slides: SlideData[] = [
    {
      id: 1,
      title: "The Clinical Application of Supplements and Nutraceuticals in Longevity Medicine: An Evidence-Based Approach for Health Optimization",
      content: (
        <div className="text-center space-y-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">
            The Clinical Application of Supplements and Nutraceuticals in Longevity Medicine: An Evidence-Based Approach for Health Optimization
          </h1>

        </div>
      )
    },
    {
      id: 2,
      title: "Learning Objectives",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Learning Objectives</h2>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <p className="text-lg text-gray-700">Understand the foundational principles of longevity medicine.</p>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-sm font-bold">2</span>
              </div>
              <p className="text-lg text-gray-700">Distinguish between supplements and nutraceuticals in clinical context.</p>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-sm font-bold">3</span>
              </div>
              <p className="text-lg text-gray-700">Review evidence-based protocols for testing, analysis, and therapy selection.</p>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-sm font-bold">4</span>
              </div>
              <p className="text-lg text-gray-700">Apply clinical guidelines for key nutraceuticals (biomarkers, dosing, monitoring).</p>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-sm font-bold">5</span>
              </div>
              <p className="text-lg text-gray-700">Critically evaluate peer-reviewed studies and recognize their limitations.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "The Shift from Lifespan to Healthspan",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Shift from Lifespan to Healthspan</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-800 mb-2">Lifespan:</h3>
                <p className="text-red-700">Years lived.</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Healthspan:</h3>
                <p className="text-green-700">Years lived in good health, free from chronic disease/disability.</p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Longevity Medicine:</h3>
                <p className="text-blue-700">Proactive, systems-based, root-cause approach to aging.</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Goal:</h3>
                <p className="text-purple-700">Maximize years of vitality, not just survival.</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <svg width="300" height="150" viewBox="0 0 300 150" className="border border-gray-200 rounded-lg">
                <rect x="20" y="60" width="260" height="30" fill="#ef4444" opacity="0.7" rx="4" />
                <text x="150" y="80" textAnchor="middle" className="fill-white font-semibold text-sm">Total Lifespan</text>
                
                <rect x="20" y="60" width="180" height="30" fill="#22c55e" opacity="0.9" rx="4" />
                <text x="110" y="80" textAnchor="middle" className="fill-white font-semibold text-sm">Healthspan</text>
                
                <text x="220" y="80" textAnchor="middle" className="fill-gray-700 font-medium text-xs">Disease/Disability</text>
                
                <text x="20" y="115" className="fill-gray-600 text-xs">Birth</text>
                <text x="260" y="115" className="fill-gray-600 text-xs">Death</text>
                
                <line x1="20" y1="105" x2="280" y2="105" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead)" />
                
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 italic">Ref: 1,2</p>
        </div>
      )
    },
    {
      id: 4,
      title: "Definitions: Supplements vs. Nutraceuticals",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Definitions: Supplements vs. Nutraceuticals</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aspect</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dietary Supplements</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nutraceuticals</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Definition</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Nutrient-based, correct deficiency</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Food-derived, broad bioactive effects</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Examples</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Vitamin D, B12, Mg</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Curcumin, Resveratrol, Omega-3s</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Regulation</td>
                  <td className="px-6 py-4 text-sm text-gray-700">FDA (DSHEA)</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Often less regulated</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Mechanism</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Direct (replace, support)</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Pleiotropic, pathway modulation</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-sm text-gray-500 italic">Ref: 3,4</p>
        </div>
      )
    },
    {
      id: 5,
      title: "Clinical Integration: Overview",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Clinical Integration: Overview</h2>
          
          <div className="space-y-4">
            <p className="text-lg text-gray-700">
              Supplements/nutraceuticals as part of a <strong>personalized, systems-based</strong> plan.
            </p>
            
            <p className="text-lg text-gray-700">
              Not stand-alone: Combine with lifestyle, detox, and regenerative therapies.
            </p>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Workflow:</h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <p className="text-gray-700">Diagnostics (biomarkers, genetics, functional tests)</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <p className="text-gray-700">Address foundational issues (e.g., gut, detox)</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <p className="text-gray-700">Targeted nutraceuticals</p>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <p className="text-gray-700">Monitoring and follow-up</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 6,
      title: "Core Tenets of Longevity Medicine",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Core Tenets of Longevity Medicine</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Proactive, Preventive Focus:</h3>
                <p className="text-blue-700">Early risk detection, subclinical dysfunction.</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Healthspan Maximization:</h3>
                <p className="text-green-700">Focus on quality, not just quantity of life.</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Biological vs. Chronological Age:</h3>
                <p className="text-purple-700">Targeting modifiable biological age.</p>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Personalized, Systems Biology:</h3>
                <p className="text-orange-700">Individualized, interconnected approach.</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <svg width="280" height="280" viewBox="0 0 280 280" className="border border-gray-200 rounded-lg">
                <circle cx="140" cy="90" r="60" fill="#3b82f6" opacity="0.3" />
                <text x="140" y="95" textAnchor="middle" className="fill-blue-800 font-semibold text-sm">Proactive</text>
                
                <circle cx="100" cy="180" r="60" fill="#10b981" opacity="0.3" />
                <text x="100" y="185" textAnchor="middle" className="fill-green-800 font-semibold text-sm">Personalized</text>
                
                <circle cx="180" cy="180" r="60" fill="#8b5cf6" opacity="0.3" />
                <text x="180" y="185" textAnchor="middle" className="fill-purple-800 font-semibold text-sm">Systems-Based</text>
                
                <circle cx="140" cy="140" r="25" fill="#f59e0b" opacity="0.8" />
                <text x="140" y="145" textAnchor="middle" className="fill-white font-bold text-xs">Longevity</text>
                <text x="140" y="155" textAnchor="middle" className="fill-white font-bold text-xs">Medicine</text>
              </svg>
            </div>
          </div>
          
          <p className="text-sm text-gray-500 italic">Ref: 1,2</p>
        </div>
      )
    },
    {
      id: 7,
      title: "Key Diagnostic Pillars in Longevity Medicine",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Key Diagnostic Pillars in Longevity Medicine</h2>
          
          <p className="text-lg text-gray-700 mb-4">
            <strong>Table 1.1: Diagnostic Pillars and Nutraceutical Relevance</strong> 
            <span className="text-sm text-gray-500">(Condensed for slide; full table in handout)</span>
          </p>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Biomarkers/Tests</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nutraceuticals (Examples)</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm">
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Inflammation</td>
                  <td className="px-4 py-3 text-gray-700">hs-CRP, IL-6, TNF-α</td>
                  <td className="px-4 py-3 text-gray-700">Curcumin, Omega-3, Resveratrol</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">Metabolic</td>
                  <td className="px-4 py-3 text-gray-700">Glucose, Insulin, HbA1c</td>
                  <td className="px-4 py-3 text-gray-700">Berberine, ALA, Omega-3</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Mitochondrial</td>
                  <td className="px-4 py-3 text-gray-700">CoQ10, ATP, OAT</td>
                  <td className="px-4 py-3 text-gray-700">CoQ10, NMN/NR, PQQ</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">Oxidative Stress</td>
                  <td className="px-4 py-3 text-gray-700">8-OHdG, GSH, SOD</td>
                  <td className="px-4 py-3 text-gray-700">NAC, Sulforaphane, Vit C/E</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Hormonal</td>
                  <td className="px-4 py-3 text-gray-700">TSH, Cortisol, Sex hormones</td>
                  <td className="px-4 py-3 text-gray-700">Ashwagandha, Zinc, Vit D</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">Nutrient Status</td>
                  <td className="px-4 py-3 text-gray-700">25(OH)D, B12, Mg, Omega-3</td>
                  <td className="px-4 py-3 text-gray-700">D3, Methyl-B12, Mg, Fish Oil</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Genetics/Epigenetics</td>
                  <td className="px-4 py-3 text-gray-700">MTHFR, APOE, DNA methylation</td>
                  <td className="px-4 py-3 text-gray-700">Methyl-Bs, Omega-3, Resveratrol</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">Gut/Microbiome</td>
                  <td className="px-4 py-3 text-gray-700">Stool, SIBO, Zonulin</td>
                  <td className="px-4 py-3 text-gray-700">Probiotics, Prebiotics</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-gray-900">Senescence</td>
                  <td className="px-4 py-3 text-gray-700">p16INK4a, SASP (research)</td>
                  <td className="px-4 py-3 text-gray-700">Fisetin, Quercetin</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="text-sm text-gray-500 italic">Ref: 1,5</p>
        </div>
      )
    },
    {
      id: 8,
      title: "Protocol: Comprehensive Biomarker Assessment",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Protocol: Comprehensive Biomarker Assessment</h2>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Stepwise Approach:</h3>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">1. Baseline Labs:</h4>
                <p className="text-blue-700">CBC, CMP, lipid panel, fasting glucose/insulin, hs-CRP, 25(OH)D, B12, RBC Mg, Omega-3 Index.</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2">2. Advanced Markers:</h4>
                <p className="text-green-700">IL-6, TNF-α, homocysteine, OAT, hormone panels.</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2">3. Specialty:</h4>
                <p className="text-purple-700">DNA methylation clocks, genetic SNPs, stool microbiome, heavy metals.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
            <h3 className="text-xl font-semibold text-yellow-900 mb-4">Interpretation:</h3>
            <ul className="space-y-2 text-yellow-800">
              <li>• Identify suboptimal/at-risk markers.</li>
              <li>• Map to aging hallmarks (e.g., inflammaging, mitochondrial dysfunction).</li>
              <li>• Select targeted nutraceuticals.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 9,
      title: "Clinical Application: Testing and Monitoring Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Clinical Application: Testing and Monitoring Protocol</h2>
          
          <div className="flex items-center justify-center mb-6">
            <svg width="400" height="250" viewBox="0 0 400 250" className="border border-gray-200 rounded-lg">
              <rect x="20" y="20" width="120" height="60" fill="#3b82f6" opacity="0.7" rx="8" />
              <text x="80" y="45" textAnchor="middle" className="fill-white font-semibold text-sm">Patient</text>
              <text x="80" y="60" textAnchor="middle" className="fill-white font-semibold text-sm">Assessment</text>
              
              <rect x="170" y="20" width="120" height="60" fill="#10b981" opacity="0.7" rx="8" />
              <text x="230" y="45" textAnchor="middle" className="fill-white font-semibold text-sm">Biomarker</text>
              <text x="230" y="60" textAnchor="middle" className="fill-white font-semibold text-sm">Panel</text>
              
              <rect x="20" y="110" width="120" height="60" fill="#8b5cf6" opacity="0.7" rx="8" />
              <text x="80" y="135" textAnchor="middle" className="fill-white font-semibold text-sm">Personalized</text>
              <text x="80" y="150" textAnchor="middle" className="fill-white font-semibold text-sm">Nutraceutical Plan</text>
              
              <rect x="170" y="110" width="120" height="60" fill="#f59e0b" opacity="0.7" rx="8" />
              <text x="230" y="135" textAnchor="middle" className="fill-white font-semibold text-sm">Follow-up Labs</text>
              <text x="230" y="150" textAnchor="middle" className="fill-white font-semibold text-sm">(2-3 months)</text>
              
              <rect x="320" y="65" width="60" height="60" fill="#ef4444" opacity="0.7" rx="8" />
              <text x="350" y="90" textAnchor="middle" className="fill-white font-semibold text-sm">Adjust</text>
              <text x="350" y="105" textAnchor="middle" className="fill-white font-semibold text-sm">Therapy</text>
              
              <defs>
                <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                </marker>
              </defs>
              
              <line x1="140" y1="50" x2="170" y2="50" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead2)" />
              <line x1="230" y1="80" x2="230" y2="110" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead2)" />
              <line x1="170" y1="140" x2="140" y2="140" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead2)" />
              <line x1="290" y1="140" x2="320" y2="110" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead2)" />
              <line x1="320" y1="95" x2="80" y2="50" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead2)" strokeDasharray="5,5" />
            </svg>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Points:</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• Re-test key markers (e.g., NAD+, Omega-3 Index, hs-CRP) at 2-3 months.</li>
              <li>• Track subjective and objective improvements.</li>
              <li>• Document and adjust.</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 10,
      title: "NAD+ Precursors (NMN/NR): Mechanisms",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">NAD+ Precursors (NMN/NR): Mechanisms</h2>
          
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">NAD+ Decline:</h3>
              <p className="text-red-700">Central to aging; affects energy, DNA repair, sirtuins.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">NMN/NR:</h3>
              <p className="text-green-700">Boost NAD+; support mitochondrial function, insulin sensitivity, neuroprotection.</p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Pathways:</h3>
              <p className="text-blue-700">Sirtuin activation, PARP support, reduced inflammation.</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center">
            <svg width="500" height="180" viewBox="0 0 500 180" className="border border-gray-200 rounded-lg">
              <rect x="20" y="70" width="80" height="40" fill="#3b82f6" opacity="0.7" rx="8" />
              <text x="60" y="85" textAnchor="middle" className="fill-white font-semibold text-sm">NR/NMN</text>
              <text x="60" y="100" textAnchor="middle" className="fill-white font-semibold text-sm">Intake</text>
              
              <rect x="130" y="70" width="80" height="40" fill="#10b981" opacity="0.7" rx="8" />
              <text x="170" y="85" textAnchor="middle" className="fill-white font-semibold text-sm">NAD+</text>
              <text x="170" y="100" textAnchor="middle" className="fill-white font-semibold text-sm">↑</text>
              
              <rect x="240" y="45" width="100" height="40" fill="#8b5cf6" opacity="0.7" rx="8" />
              <text x="290" y="60" textAnchor="middle" className="fill-white font-semibold text-sm">SIRT1/PARP</text>
              <text x="290" y="75" textAnchor="middle" className="fill-white font-semibold text-sm">↑</text>
              
              <rect x="240" y="95" width="100" height="65" fill="#f59e0b" opacity="0.7" rx="8" />
              <text x="290" y="115" textAnchor="middle" className="fill-white font-semibold text-xs">DNA repair</text>
              <text x="290" y="130" textAnchor="middle" className="fill-white font-semibold text-xs">Mitochondrial</text>
              <text x="290" y="145" textAnchor="middle" className="fill-white font-semibold text-xs">biogenesis</text>
              
              <rect x="370" y="70" width="100" height="40" fill="#ef4444" opacity="0.7" rx="8" />
              <text x="420" y="85" textAnchor="middle" className="fill-white font-semibold text-sm">Inflammation</text>
              <text x="420" y="100" textAnchor="middle" className="fill-white font-semibold text-sm">↓</text>
              
              <defs>
                <marker id="arrowhead3" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                </marker>
              </defs>
              
              <line x1="100" y1="90" x2="130" y2="90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead3)" />
              <line x1="210" y1="90" x2="240" y2="90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead3)" />
              <line x1="340" y1="90" x2="370" y2="90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead3)" />
              <line x1="210" y1="85" x2="240" y2="65" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead3)" />
              <line x1="210" y1="95" x2="240" y2="127" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead3)" />
            </svg>
          </div>
          
          <p className="text-sm text-gray-500 italic">Ref: 6,7,13</p>
        </div>
      )
    }
    // Continue with slides 11-34...
  ];

  // Add remaining slides 11-34
  const remainingSlides: SlideData[] = [
    {
      id: 11,
      title: "NAD+ Precursors: Clinical Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">NAD+ Precursors: Clinical Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Improved metabolism, neuroprotection, lifespan extension (mice).</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Human Trials:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• ↑ NAD+ (up to 142% with NR 1000mg/d) (17)</li>
                <li>• Improved insulin sensitivity (NMN 250mg/d, 10wks, prediabetic women) (7)</li>
                <li>• Mixed results on clinical endpoints in healthy adults.</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-3">Critique:</h3>
              <ul className="space-y-2 text-yellow-700">
                <li>• Short duration, small N, mostly surrogate endpoints.</li>
                <li>• Gut microbiome may affect efficacy (13).</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 12,
      title: "NAD+ Precursors: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">NAD+ Precursors: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Metabolic syndrome, fatigue, neuroprotection</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">NAD+ (LC-MS/MS), glucose/insulin, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">NR: 250-1000mg/d; NMN: 250-1000mg/d</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Resveratrol, TMG (methylation), antioxidants</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Monitoring</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Re-check NAD+, metabolic markers @ 2-3 mo</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Cancer (theoretical), methylation strain</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 13,
      title: "Resveratrol: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Resveratrol: Mechanisms & Evidence</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Mechanisms:</h3>
                <p className="text-blue-700">SIRT1/AMPK activation, mTOR inhibition, NF-κB inhibition, antioxidant.</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
                <p className="text-green-700">Lifespan extension (yeast, worms, some mice), improved metabolism, neuroprotection.</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Clinical:</h3>
                <ul className="text-purple-700 space-y-1">
                  <li>• Modest metabolic benefits (T2DM, MetS).</li>
                  <li>• Poor oral bioavailability limits systemic effects.</li>
                  <li>• Topical: skin aging/wound healing.</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
                <p className="text-yellow-700">Small, short trials; high-dose GI side effects.</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <svg width="280" height="300" viewBox="0 0 280 300" className="border border-gray-200 rounded-lg">
                <rect x="90" y="20" width="100" height="40" fill="#8b5cf6" opacity="0.7" rx="8" />
                <text x="140" y="45" textAnchor="middle" className="fill-white font-semibold text-sm">Resveratrol</text>
                
                <rect x="20" y="90" width="80" height="35" fill="#3b82f6" opacity="0.7" rx="6" />
                <text x="60" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">SIRT1/AMPK</text>
                
                <rect x="180" y="90" width="80" height="35" fill="#10b981" opacity="0.7" rx="6" />
                <text x="220" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">mTOR↓</text>
                
                <rect x="20" y="150" width="80" height="35" fill="#f59e0b" opacity="0.7" rx="6" />
                <text x="60" y="170" textAnchor="middle" className="fill-white font-semibold text-xs">NF-κB↓</text>
                
                <rect x="180" y="150" width="80" height="35" fill="#ef4444" opacity="0.7" rx="6" />
                <text x="220" y="170" textAnchor="middle" className="fill-white font-semibold text-xs">Antioxidant</text>
                
                <rect x="90" y="220" width="100" height="50" fill="#22c55e" opacity="0.7" rx="8" />
                <text x="140" y="240" textAnchor="middle" className="fill-white font-semibold text-xs">Longevity</text>
                <text x="140" y="255" textAnchor="middle" className="fill-white font-semibold text-xs">Benefits</text>
                
                <defs>
                  <marker id="arrowhead4" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                  </marker>
                </defs>
                
                <line x1="120" y1="60" x2="80" y2="90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="160" y1="60" x2="200" y2="90" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="120" y1="60" x2="80" y2="150" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="160" y1="60" x2="200" y2="150" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="60" y1="125" x2="120" y2="220" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="220" y1="125" x2="160" y2="220" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="60" y1="185" x2="120" y2="220" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
                <line x1="220" y1="185" x2="160" y2="220" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead4)" />
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 14,
      title: "Resveratrol: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Resveratrol: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Metabolic, CVD, skin aging, neuroprotection</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Glucose, lipids, hs-CRP, skin assessment</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">150-500mg/d (oral); 0.5-5% (topical)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">NMN/NR, piperine, pterostilbene</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">CYP450 interactions, bleeding risk, GI upset</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 15,
      title: "Berberine: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Berberine: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Mechanisms:</h3>
              <p className="text-blue-700">AMPK activation, SIRT1, PI3K-Akt, NF-κB inhibition, gut microbiome modulation.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Lifespan extension (C. elegans), improved cognition, anti-inflammatory.</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-purple-700">
                <li>• Lowers glucose, HbA1c, lipids (comparable to metformin).</li>
                <li>• Reduces inflammation (hs-CRP, IL-6).</li>
                <li>• Poor oral bioavailability; gut microbiome crucial.</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Berberine demonstrates metformin-like metabolic benefits through AMPK activation and gut microbiome modulation, making it particularly valuable for metabolic syndrome and inflammatory conditions.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 16,
      title: "Berberine: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Berberine: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">T2DM, MetS, NAFLD, dyslipidemia, PCOS</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Glucose, HbA1c, lipids, LFTs, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">500mg 2-3x/d with meals (1000-1500mg/d)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Silymarin, ALA, probiotics</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">CYP450 interactions, GI upset, hypoglycemia, avoid in pregnancy</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 17,
      title: "Fisetin: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Fisetin: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">Mechanisms:</h3>
              <p className="text-red-700">Senolytic (PI3K/Akt/mTOR inhibition), antioxidant (Nrf2), anti-inflammatory, iron chelation.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Reduces senescent cells, extends lifespan (mice), neuroprotection.</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• Ongoing trials (osteoarthritis, MCI, frailty).</li>
                <li>• "Hit-and-run" high-dose intermittent protocols.</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Human data emerging; optimal dosing/frequency TBD.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Fisetin is one of the most potent natural senolytics, showing promise for clearing senescent cells and extending healthspan, though optimal clinical protocols are still being established.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 18,
      title: "Fisetin: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Fisetin: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Senescence, frailty, neuroprotection</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">SASP (IL-6, MMPs), hs-CRP, function tests</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">20mg/kg/d x 2-3d/month (e.g., 1400mg/d)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Autophagy enhancers, antioxidants</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bleeding risk, CYP450, liver/kidney function</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mt-4">
            <h3 className="font-semibold text-orange-800 mb-2">Protocol Note:</h3>
            <p className="text-orange-700">Intermittent "hit-and-run" dosing mimics successful preclinical protocols and may minimize potential side effects while maximizing senolytic efficacy.</p>
          </div>
        </div>
      )
    },
    {
      id: 19,
      title: "Quercetin (± Dasatinib): Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Quercetin (± Dasatinib): Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Mechanisms:</h3>
              <p className="text-purple-700">Senolytic (SCAP inhibition), SIRT1/AMPK activation, antioxidant, anti-inflammatory, JAK/STAT inhibition.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">D+Q clears senescent cells, extends healthspan.</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• D+Q reduces senescent cell burden, improves function (IPF, DKD, MCI).</li>
                <li>• Bioavailability variable; phytosome forms better.</li>
              </ul>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">Critique:</h3>
              <p className="text-red-700">Small, open-label studies; D+Q safety (Dasatinib is a chemo drug).</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The Dasatinib + Quercetin combination represents the most clinically advanced senolytic protocol, though dasatinib's chemotherapy background requires careful safety monitoring.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 20,
      title: "Quercetin: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Quercetin: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Senescence (D+Q), inflammation, allergies</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">SASP, function, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">D+Q: 100mg Dasatinib + 1000-1250mg Quercetin x 2-3d/mo</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Dasatinib, Vitamin C, bromelain</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Dasatinib toxicity, CYP3A4 interactions, kidney function</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200 mt-4">
            <h3 className="font-semibold text-red-800 mb-2">Safety Warning:</h3>
            <p className="text-red-700">Dasatinib requires careful monitoring due to potential for thrombocytopenia, bleeding, and other chemotherapy-related side effects. Consider quercetin alone for lower-risk patients.</p>
          </div>
        </div>
      )
    },
    {
      id: 21,
      title: "Spermidine: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Spermidine: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Mechanisms:</h3>
              <p className="text-green-700">Autophagy induction (EP300 inhibition), anti-inflammatory, histone deacetylation, lipid metabolism.</p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Preclinical:</h3>
              <p className="text-blue-700">Lifespan extension (yeast, flies, mice), improved cardiac, neuro, muscle health.</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-purple-700">
                <li>• Higher dietary intake linked to lower mortality (Bruneck Study).</li>
                <li>• Improved memory in elderly (RCTs).</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Mostly epidemiological/human pilot data; optimal dosing unclear.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Spermidine is one of the few compounds with both strong epidemiological evidence for longevity and demonstrated autophagy induction, making it particularly promising for healthy aging interventions.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 22,
      title: "Spermidine: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Spermidine: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Longevity, CVD, cognition, inflammaging</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Inflammation, cognitive tests, CVD risk</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">1-10mg/d (wheat germ extract)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Autophagy enhancers, mitochondrial nutrients</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">GI upset, wheat allergy</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 23,
      title: "Omega-3 Fatty Acids (EPA/DHA): Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Omega-3 Fatty Acids (EPA/DHA): Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Mechanisms:</h3>
              <p className="text-blue-700">Anti-inflammatory (SPMs), PPARα activation, mitochondrial support, mTOR modulation, telomere/epigenetic aging.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Organ aging slowed, muscle/brain/cardiac protection.</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-purple-700">
                <li>• ↓ Triglycerides, CVD events (high-dose EPA), inflammation.</li>
                <li>• Slows epigenetic aging (DO-HEALTH: 1g/d, 3yrs).</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Results vary by dose, EPA:DHA ratio, population.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Omega-3 fatty acids have the strongest clinical evidence for longevity benefits, with proven cardiovascular protection and emerging evidence for epigenetic age slowing.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 24,
      title: "Omega-3: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Omega-3: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">CVD, inflammation, cognition, sarcopenia</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Omega-3 Index ({'>'}8%), lipids, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">1-4g/d EPA+DHA (dose by indication)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Vitamin D, antioxidants, curcumin</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bleeding risk ({'>'}3g/d), GI upset, LDL↑</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
            <h3 className="font-semibold text-green-800 mb-2">Target Goal:</h3>
            <p className="text-green-700">Omega-3 Index {'>'}8% is associated with optimal cardiovascular and longevity outcomes. Most patients require 2-3g/d EPA+DHA to achieve this target.</p>
          </div>
        </div>
      )
    },
    {
      id: 25,
      title: "Vitamin D: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Vitamin D: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="font-semibold text-orange-800 mb-2">Mechanisms:</h3>
              <p className="text-orange-700">VDR-mediated gene regulation, immune modulation, anti-inflammatory, Nrf2 activation, mitochondrial support, senescence inhibition.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Deficiency accelerates aging; supplementation extends lifespan (mice).</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• Reduces all-cause mortality (meta-analyses).</li>
                <li>• Slows epigenetic aging (DO-HEALTH, additive with Omega-3).</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">RCTs may be underdosed, not targeted to deficiency.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Vitamin D deficiency is pandemic in modern society and strongly associated with accelerated aging. Optimization to 40-70 ng/mL appears essential for longevity medicine protocols.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 26,
      title: "Vitamin D: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Vitamin D: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bone, immune, CVD, mood, longevity</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">25(OH)D (target 40-70ng/mL), Ca, PTH</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">2000-5000 IU/d (higher to correct deficiency)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Omega-3, Mg, K2</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Hypercalcemia, kidney stones, drug interactions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 27,
      title: "Magnesium: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Magnesium: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Mechanisms:</h3>
              <p className="text-purple-700">ATP synthesis, DNA repair, telomere stability, insulin signaling, mitochondrial function, anti-inflammatory.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Supplementation improves muscle, mitochondrial function, lifespan (mice).</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• Deficiency common in elderly; linked to CVD, T2DM, osteoporosis, frailty.</li>
                <li>• Supplementation lowers BP, improves insulin sensitivity, muscle function.</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Serum Mg poor marker; use RBC Mg.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Magnesium deficiency is ubiquitous in aging populations and contributes to multiple age-related pathologies. RBC magnesium is the preferred biomarker for assessment.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 28,
      title: "Magnesium: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Magnesium: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Deficiency, CVD, T2DM, osteoporosis, sleep</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">RBC Mg, serum Mg, BP, glucose</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">200-400mg/d elemental Mg (glycinate, malate, citrate)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Vitamin D, K2, Omega-3</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Renal impairment, diarrhea (form-dependent)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 29,
      title: "Integrative Clinical Workflow",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Integrative Clinical Workflow</h2>
          
          <div className="flex items-center justify-center mb-6">
            <svg width="500" height="300" viewBox="0 0 500 300" className="border border-gray-200 rounded-lg">
              <circle cx="250" cy="150" r="120" fill="none" stroke="#6b7280" strokeWidth="2" strokeDasharray="10,5" />
              
              <rect x="200" y="40" width="100" height="40" fill="#3b82f6" opacity="0.7" rx="8" />
              <text x="250" y="65" textAnchor="middle" className="fill-white font-semibold text-sm">Diagnostics</text>
              
              <rect x="320" y="90" width="100" height="50" fill="#10b981" opacity="0.7" rx="8" />
              <text x="370" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">Foundational</text>
              <text x="370" y="125" textAnchor="middle" className="fill-white font-semibold text-xs">(gut, detox)</text>
              
              <rect x="320" y="180" width="100" height="50" fill="#8b5cf6" opacity="0.7" rx="8" />
              <text x="370" y="200" textAnchor="middle" className="fill-white font-semibold text-xs">Targeted</text>
              <text x="370" y="215" textAnchor="middle" className="fill-white font-semibold text-xs">Nutraceuticals</text>
              
              <rect x="200" y="240" width="100" height="50" fill="#f59e0b" opacity="0.7" rx="8" />
              <text x="250" y="260" textAnchor="middle" className="fill-white font-semibold text-xs">Lifestyle/</text>
              <text x="250" y="275" textAnchor="middle" className="fill-white font-semibold text-xs">Regenerative</text>
              
              <rect x="80" y="180" width="100" height="40" fill="#ef4444" opacity="0.7" rx="8" />
              <text x="130" y="205" textAnchor="middle" className="fill-white font-semibold text-sm">Monitoring</text>
              
              <rect x="80" y="90" width="100" height="40" fill="#22c55e" opacity="0.7" rx="8" />
              <text x="130" y="115" textAnchor="middle" className="fill-white font-semibold text-sm">Maintenance</text>
              
              <defs>
                <marker id="arrowhead5" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#6b7280" />
                </marker>
              </defs>
              
              <line x1="280" y1="80" x2="340" y2="110" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead5)" />
              <line x1="380" y1="140" x2="380" y2="180" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead5)" />
              <line x1="340" y1="220" x2="280" y2="250" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead5)" />
              <line x1="200" y1="260" x2="160" y2="210" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead5)" />
              <line x1="130" y1="180" x2="130" y2="130" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead5)" />
              <line x1="160" y1="100" x2="200" y2="70" stroke="#6b7280" strokeWidth="2" markerEnd="url(#arrowhead5)" />
            </svg>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Personalize:</h3>
              <p className="text-blue-700">Adjust for genetics, age, comorbidities.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Monitor:</h3>
              <p className="text-green-700">Biomarkers, function, subjective outcomes.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Adjust:</h3>
              <p className="text-purple-700">Based on labs, side effects, goals.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 30,
      title: "Case Example",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Case Example</h2>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Patient:</h3>
            <p className="text-gray-700 mb-4">65M, fatigue, prediabetes, low Omega-3 Index, hs-CRP 4.0, 25(OH)D 18ng/mL, NAD+ low.</p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Plan:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Supplements:</h4>
                <ul className="text-blue-700 space-y-1">
                  <li>• NMN 500mg/d</li>
                  <li>• Omega-3 2g/d</li>
                  <li>• Vitamin D3 5000IU/d</li>
                  <li>• Mg 300mg/d</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2">Monitoring:</h4>
                <ul className="text-green-700 space-y-1">
                  <li>• Recheck labs at 3 months</li>
                  <li>• Add berberine if glucose/HbA1c not improved</li>
                  <li>• Mediterranean diet</li>
                  <li>• Exercise program</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            This case demonstrates personalized nutraceutical selection based on specific biomarker abnormalities, with clear monitoring and adjustment protocols.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 31,
      title: "Summary & Key Takeaways",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Summary & Key Takeaways</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-700">Longevity medicine is proactive, personalized, and systems-based.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-green-700">Supplements/nutraceuticals can target aging hallmarks, but require evidence-based, individualized protocols.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-purple-700">Clinical application: test, interpret, personalize, monitor, adjust.</p>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-orange-700">Most robust evidence: Omega-3s, Vitamin D, berberine for metabolic/CVD; emerging: NAD+ precursors, senolytics, spermidine.</p>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-yellow-700">Always consider safety, drug interactions, and patient context.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The future of longevity medicine lies in precision nutraceutical protocols guided by biomarkers, genetics, and individual aging patterns rather than one-size-fits-all approaches.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 32,
      title: "References",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">References</h2>
          
          <p className="text-lg text-gray-600 mb-6 italic">(Abbreviated for slide; full list in handout)</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <ol className="space-y-2 text-sm text-gray-700">
                <li>1. Physicians Assistance: Longevity Medicine, 2025</li>
                <li>2. DCIMedicine: Longevity Medicine, 2025</li>
                <li>3. PMC9654660</li>
                <li>4. Vantage Nutrition, 2025</li>
                <li>5. DO-HEALTH, PubMed 39900648</li>
                <li>6. PMC7963035</li>
                <li>7. PMC8612620</li>
                <li>13. PubMed 40117359</li>
                <li>17. medRxiv 2025.03.19.25324259</li>
              </ol>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-700 font-medium">...</p>
              <p className="text-blue-700 mt-2">[See handout for full reference list]</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 33,
      title: "Q&A",
      content: (
        <div className="text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Thank you!</h2>
          
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-lg border border-blue-200">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Questions?</h3>
            <p className="text-lg text-gray-600">[Contact info]</p>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <p className="text-gray-700">
              For additional resources and detailed protocols, please refer to the comprehensive handout materials.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 34,
      title: "Clinical Application of Longevity Nutraceuticals: SHIFT Framework Summary",
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Clinical Application of Longevity Nutraceuticals: SHIFT Framework Summary</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">SHIFT Domain</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Key Pathways</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Nutraceuticals</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Mechanisms</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Clinical Protocols</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase">Biomarkers</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">Sensing & Metabolic Fuel-Gauging</td>
                  <td className="px-2 py-2 text-gray-700">Glucose/insulin homeostasis, Mitochondrial function, AMPK/SIRT1/mTOR</td>
                  <td className="px-2 py-2 text-gray-700">Berberine, NAD+ Precursors, Resveratrol, Omega-3s, Magnesium</td>
                  <td className="px-2 py-2 text-gray-700">AMPK activation, SIRT1 activation, NAD+ replenishment</td>
                  <td className="px-2 py-2 text-gray-700">Berberine: 500mg 2-3x/d, NMN/NR: 250-1000mg/d</td>
                  <td className="px-2 py-2 text-gray-700">Glucose, insulin, HbA1c, NAD+</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900">Homeostatic Defence</td>
                  <td className="px-2 py-2 text-gray-700">DNA repair, Stem cell maintenance, Hormonal balance</td>
                  <td className="px-2 py-2 text-gray-700">NAD+ Precursors, Resveratrol, Vitamin D, Spermidine</td>
                  <td className="px-2 py-2 text-gray-700">PARP activation, VDR-mediated regulation, Autophagy</td>
                  <td className="px-2 py-2 text-gray-700">Vit D: 2000-5000 IU/d, Spermidine: 1-10mg/d</td>
                  <td className="px-2 py-2 text-gray-700">25(OH)D, DNA methylation clocks</td>
                </tr>
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">Inflammation</td>
                  <td className="px-2 py-2 text-gray-700">Chronic inflammation, Immune modulation, SASP reduction</td>
                  <td className="px-2 py-2 text-gray-700">Omega-3s, Curcumin, Resveratrol, Quercetin</td>
                  <td className="px-2 py-2 text-gray-700">NF-κB inhibition, SPMs production</td>
                  <td className="px-2 py-2 text-gray-700">Omega-3s: 1-4g/d, Curcumin: 500-1500mg/d</td>
                  <td className="px-2 py-2 text-gray-700">hs-CRP, IL-6, TNF-α</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-2 py-2 font-medium text-gray-900">Fibrosis</td>
                  <td className="px-2 py-2 text-gray-700">ECM turnover, Senescent cell clearance</td>
                  <td className="px-2 py-2 text-gray-700">Fisetin, Quercetin, Resveratrol</td>
                  <td className="px-2 py-2 text-gray-700">Senolytic action, MMP inhibition</td>
                  <td className="px-2 py-2 text-gray-700">Fisetin: 20mg/kg/d x2-3d/mo</td>
                  <td className="px-2 py-2 text-gray-700">SASP factors, p16INK4a</td>
                </tr>
                <tr>
                  <td className="px-2 py-2 font-medium text-gray-900">Timekeeping</td>
                  <td className="px-2 py-2 text-gray-700">Circadian rhythms, Epigenetic clocks</td>
                  <td className="px-2 py-2 text-gray-700">NAD+ Precursors, Vitamin D, Melatonin, Magnesium</td>
                  <td className="px-2 py-2 text-gray-700">Clock gene modulation, Sleep regulation</td>
                  <td className="px-2 py-2 text-gray-700">Melatonin: 0.5-3mg qHS, Mg: 200-400mg/d</td>
                  <td className="px-2 py-2 text-gray-700">DNA methylation clocks, Sleep scores</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <KeyTakeaway>
            The SHIFT framework provides a comprehensive approach to longevity nutraceuticals, enabling targeted interventions based on individual biomarker profiles and aging mechanisms. Regular monitoring and protocol adjustment are essential for optimal outcomes.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 13,
      title: "Resveratrol: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Resveratrol: Mechanisms & Evidence</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Mechanisms:</h3>
                <p className="text-blue-700">SIRT1/AMPK activation, mTOR inhibition, NF-κB inhibition, antioxidant.</p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
                <p className="text-green-700">Lifespan extension (yeast, worms, some mice), improved metabolism, neuroprotection.</p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Clinical:</h3>
                <ul className="text-purple-700 space-y-1">
                  <li>• Modest metabolic benefits (T2DM, MetS).</li>
                  <li>• Poor oral bioavailability limits systemic effects.</li>
                  <li>• Topical: skin aging/wound healing.</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
                <p className="text-yellow-700">Small, short trials; high-dose GI side effects.</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <svg width="280" height="300" viewBox="0 0 280 300" className="border border-gray-200 rounded-lg">
                <rect x="90" y="20" width="100" height="40" fill="#8b5cf6" opacity="0.7" rx="8" />
                <text x="140" y="45" textAnchor="middle" className="fill-white font-semibold text-sm">Resveratrol</text>
                
                <rect x="20" y="90" width="80" height="35" fill="#3b82f6" opacity="0.7" rx="6" />
                <text x="60" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">SIRT1/AMPK</text>
                
                <rect x="180" y="90" width="80" height="35" fill="#10b981" opacity="0.7" rx="6" />
                <text x="220" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">mTOR↓</text>
                
                <rect x="20" y="150" width="80" height="35" fill="#f59e0b" opacity="0.7" rx="6" />
                <text x="60" y="170" textAnchor="middle" className="fill-white font-semibold text-xs">NF-κB↓</text>
                
                <rect x="180" y="150" width="80" height="35" fill="#ef4444" opacity="0.7" rx="6" />
                <text x="220" y="170" textAnchor="middle" className="fill-white font-semibold text-xs">Antioxidant</text>
                
                <rect x="90" y="220" width="100" height="50" fill="#22c55e" opacity="0.7" rx="8" />
                <text x="140" y="240" textAnchor="middle" className="fill-white font-semibold text-xs">Longevity</text>
                <text x="140" y="255" textAnchor="middle" className="fill-white font-semibold text-xs">Benefits</text>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 14,
      title: "Resveratrol: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Resveratrol: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Metabolic, CVD, skin aging, neuroprotection</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Glucose, lipids, hs-CRP, skin assessment</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">150-500mg/d (oral); 0.5-5% (topical)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">NMN/NR, piperine, pterostilbene</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">CYP450 interactions, bleeding risk, GI upset</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 15,
      title: "Berberine: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Berberine: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Mechanisms:</h3>
              <p className="text-blue-700">AMPK activation, SIRT1, PI3K-Akt, NF-κB inhibition, gut microbiome modulation.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Lifespan extension (C. elegans), improved cognition, anti-inflammatory.</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-purple-700">
                <li>• Lowers glucose, HbA1c, lipids (comparable to metformin).</li>
                <li>• Reduces inflammation (hs-CRP, IL-6).</li>
                <li>• Poor oral bioavailability; gut microbiome crucial.</li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            Berberine demonstrates metformin-like metabolic benefits through AMPK activation and gut microbiome modulation, making it particularly valuable for metabolic syndrome and inflammatory conditions.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 16,
      title: "Berberine: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Berberine: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">T2DM, MetS, NAFLD, dyslipidemia, PCOS</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Glucose, HbA1c, lipids, LFTs, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">500mg 2-3x/d with meals (1000-1500mg/d)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Silymarin, ALA, probiotics</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">CYP450 interactions, GI upset, hypoglycemia, avoid in pregnancy</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 17,
      title: "Fisetin: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Fisetin: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">Mechanisms:</h3>
              <p className="text-red-700">Senolytic (PI3K/Akt/mTOR inhibition), antioxidant (Nrf2), anti-inflammatory, iron chelation.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Reduces senescent cells, extends lifespan (mice), neuroprotection.</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• Ongoing trials (osteoarthritis, MCI, frailty).</li>
                <li>• "Hit-and-run" high-dose intermittent protocols.</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Human data emerging; optimal dosing/frequency TBD.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Fisetin is one of the most potent natural senolytics, showing promise for clearing senescent cells and extending healthspan, though optimal clinical protocols are still being established.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 18,
      title: "Fisetin: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Fisetin: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Senescence, frailty, neuroprotection</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">SASP (IL-6, MMPs), hs-CRP, function tests</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">20mg/kg/d x 2-3d/month (e.g., 1400mg/d)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Autophagy enhancers, antioxidants</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bleeding risk, CYP450, liver/kidney function</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mt-4">
            <h3 className="font-semibold text-orange-800 mb-2">Protocol Note:</h3>
            <p className="text-orange-700">Intermittent "hit-and-run" dosing mimics successful preclinical protocols and may minimize potential side effects while maximizing senolytic efficacy.</p>
          </div>
        </div>
      )
    },
    {
      id: 19,
      title: "Quercetin (± Dasatinib): Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Quercetin (± Dasatinib): Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Mechanisms:</h3>
              <p className="text-purple-700">Senolytic (SCAP inhibition), SIRT1/AMPK activation, antioxidant, anti-inflammatory, JAK/STAT inhibition.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">D+Q clears senescent cells, extends healthspan.</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• D+Q reduces senescent cell burden, improves function (IPF, DKD, MCI).</li>
                <li>• Bioavailability variable; phytosome forms better.</li>
              </ul>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h3 className="font-semibold text-red-800 mb-2">Critique:</h3>
              <p className="text-red-700">Small, open-label studies; D+Q safety (Dasatinib is a chemo drug).</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The Dasatinib + Quercetin combination represents the most clinically advanced senolytic protocol, though dasatinib's chemotherapy background requires careful safety monitoring.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 20,
      title: "Quercetin: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Quercetin: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Senescence (D+Q), inflammation, allergies</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">SASP, function, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">D+Q: 100mg Dasatinib + 1000-1250mg Quercetin x 2-3d/mo</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Dasatinib, Vitamin C, bromelain</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Dasatinib toxicity, CYP3A4 interactions, kidney function</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200 mt-4">
            <h3 className="font-semibold text-red-800 mb-2">Safety Warning:</h3>
            <p className="text-red-700">Dasatinib requires careful monitoring due to potential for thrombocytopenia, bleeding, and other chemotherapy-related side effects. Consider quercetin alone for lower-risk patients.</p>
          </div>
        </div>
      )
    },
    {
      id: 21,
      title: "Spermidine: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Spermidine: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Mechanisms:</h3>
              <p className="text-green-700">Autophagy induction (EP300 inhibition), anti-inflammatory, histone deacetylation, lipid metabolism.</p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Preclinical:</h3>
              <p className="text-blue-700">Lifespan extension (yeast, flies, mice), improved cardiac, neuro, muscle health.</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-purple-700">
                <li>• Higher dietary intake linked to lower mortality (Bruneck Study).</li>
                <li>• Improved memory in elderly (RCTs).</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Mostly epidemiological/human pilot data; optimal dosing unclear.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Spermidine is one of the few compounds with both strong epidemiological evidence for longevity and demonstrated autophagy induction, making it particularly promising for healthy aging interventions.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 22,
      title: "Spermidine: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Spermidine: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Longevity, CVD, cognition, inflammaging</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Inflammation, cognitive tests, CVD risk</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">1-10mg/d (wheat germ extract)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Autophagy enhancers, mitochondrial nutrients</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">GI upset, wheat allergy</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 23,
      title: "Omega-3 Fatty Acids (EPA/DHA): Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Omega-3 Fatty Acids (EPA/DHA): Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Mechanisms:</h3>
              <p className="text-blue-700">Anti-inflammatory (SPMs), PPARα activation, mitochondrial support, mTOR modulation, telomere/epigenetic aging.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Organ aging slowed, muscle/brain/cardiac protection.</p>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-purple-700">
                <li>• ↓ Triglycerides, CVD events (high-dose EPA), inflammation.</li>
                <li>• Slows epigenetic aging (DO-HEALTH: 1g/d, 3yrs).</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Results vary by dose, EPA:DHA ratio, population.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Omega-3 fatty acids have the strongest clinical evidence for longevity benefits, with proven cardiovascular protection and emerging evidence for epigenetic age slowing.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 24,
      title: "Omega-3: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Omega-3: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">CVD, inflammation, cognition, sarcopenia</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Omega-3 Index ({'>'}8%), lipids, hs-CRP</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">1-4g/d EPA+DHA (dose by indication)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Vitamin D, antioxidants, curcumin</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bleeding risk ({'>'}3g/d), GI upset, LDL↑</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
            <h3 className="font-semibold text-green-800 mb-2">Target Goal:</h3>
            <p className="text-green-700">Omega-3 Index {'>'}8% is associated with optimal cardiovascular and longevity outcomes. Most patients require 2-3g/d EPA+DHA to achieve this target.</p>
          </div>
        </div>
      )
    },
    {
      id: 25,
      title: "Vitamin D: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Vitamin D: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h3 className="font-semibold text-orange-800 mb-2">Mechanisms:</h3>
              <p className="text-orange-700">VDR-mediated gene regulation, immune modulation, anti-inflammatory, Nrf2 activation, mitochondrial support, senescence inhibition.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Deficiency accelerates aging; supplementation extends lifespan (mice).</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• Reduces all-cause mortality (meta-analyses).</li>
                <li>• Slows epigenetic aging (DO-HEALTH, additive with Omega-3).</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">RCTs may be underdosed, not targeted to deficiency.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Vitamin D deficiency is pandemic in modern society and strongly associated with accelerated aging. Optimization to 40-70 ng/mL appears essential for longevity medicine protocols.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 26,
      title: "Vitamin D: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Vitamin D: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Bone, immune, CVD, mood, longevity</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">25(OH)D (target 40-70ng/mL), Ca, PTH</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">2000-5000 IU/d (higher to correct deficiency)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Omega-3, Mg, K2</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Hypercalcemia, kidney stones, drug interactions</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 27,
      title: "Magnesium: Mechanisms & Evidence",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Magnesium: Mechanisms & Evidence</h2>
          
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Mechanisms:</h3>
              <p className="text-purple-700">ATP synthesis, DNA repair, telomere stability, insulin signaling, mitochondrial function, anti-inflammatory.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Preclinical:</h3>
              <p className="text-green-700">Supplementation improves muscle, mitochondrial function, lifespan (mice).</p>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-3">Clinical:</h3>
              <ul className="space-y-2 text-blue-700">
                <li>• Deficiency common in elderly; linked to CVD, T2DM, osteoporosis, frailty.</li>
                <li>• Supplementation lowers BP, improves insulin sensitivity, muscle function.</li>
              </ul>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">Critique:</h3>
              <p className="text-yellow-700">Serum Mg poor marker; use RBC Mg.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            Magnesium deficiency is ubiquitous in aging populations and contributes to multiple age-related pathologies. RBC magnesium is the preferred biomarker for assessment.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 28,
      title: "Magnesium: Clinical Protocol",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Magnesium: Clinical Protocol</h2>
          
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Indications</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Deficiency, CVD, T2DM, osteoporosis, sleep</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Biomarkers</td>
                  <td className="px-6 py-4 text-sm text-gray-700">RBC Mg, serum Mg, BP, glucose</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Dosing</td>
                  <td className="px-6 py-4 text-sm text-gray-700">200-400mg/d elemental Mg (glycinate, malate, citrate)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Synergy</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Vitamin D, K2, Omega-3</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cautions</td>
                  <td className="px-6 py-4 text-sm text-gray-700">Renal impairment, diarrhea (form-dependent)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )
    },
    {
      id: 29,
      title: "Integrative Clinical Workflow",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Integrative Clinical Workflow</h2>
          
          <div className="flex items-center justify-center mb-6">
            <svg width="500" height="300" viewBox="0 0 500 300" className="border border-gray-200 rounded-lg">
              <circle cx="250" cy="150" r="120" fill="none" stroke="#6b7280" strokeWidth="2" strokeDasharray="10,5" />
              
              <rect x="200" y="40" width="100" height="40" fill="#3b82f6" opacity="0.7" rx="8" />
              <text x="250" y="65" textAnchor="middle" className="fill-white font-semibold text-sm">Diagnostics</text>
              
              <rect x="320" y="90" width="100" height="50" fill="#10b981" opacity="0.7" rx="8" />
              <text x="370" y="110" textAnchor="middle" className="fill-white font-semibold text-xs">Foundational</text>
              <text x="370" y="125" textAnchor="middle" className="fill-white font-semibold text-xs">(gut, detox)</text>
              
              <rect x="320" y="180" width="100" height="50" fill="#8b5cf6" opacity="0.7" rx="8" />
              <text x="370" y="200" textAnchor="middle" className="fill-white font-semibold text-xs">Targeted</text>
              <text x="370" y="215" textAnchor="middle" className="fill-white font-semibold text-xs">Nutraceuticals</text>
              
              <rect x="200" y="240" width="100" height="50" fill="#f59e0b" opacity="0.7" rx="8" />
              <text x="250" y="260" textAnchor="middle" className="fill-white font-semibold text-xs">Lifestyle/</text>
              <text x="250" y="275" textAnchor="middle" className="fill-white font-semibold text-xs">Regenerative</text>
              
              <rect x="80" y="180" width="100" height="40" fill="#ef4444" opacity="0.7" rx="8" />
              <text x="130" y="205" textAnchor="middle" className="fill-white font-semibold text-sm">Monitoring</text>
              
              <rect x="80" y="90" width="100" height="40" fill="#22c55e" opacity="0.7" rx="8" />
              <text x="130" y="115" textAnchor="middle" className="fill-white font-semibold text-sm">Maintenance</text>
            </svg>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Personalize:</h3>
              <p className="text-blue-700">Adjust for genetics, age, comorbidities.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">Monitor:</h3>
              <p className="text-green-700">Biomarkers, function, subjective outcomes.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-800 mb-2">Adjust:</h3>
              <p className="text-purple-700">Based on labs, side effects, goals.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 30,
      title: "Case Example",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Case Example</h2>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Patient:</h3>
            <p className="text-gray-700 mb-4">65M, fatigue, prediabetes, low Omega-3 Index, hs-CRP 4.0, 25(OH)D 18ng/mL, NAD+ low.</p>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Plan:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Supplements:</h4>
                <ul className="text-blue-700 space-y-1">
                  <li>• NMN 500mg/d</li>
                  <li>• Omega-3 2g/d</li>
                  <li>• Vitamin D3 5000IU/d</li>
                  <li>• Mg 300mg/d</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2">Monitoring:</h4>
                <ul className="text-green-700 space-y-1">
                  <li>• Recheck labs at 3 months</li>
                  <li>• Add berberine if glucose/HbA1c not improved</li>
                  <li>• Mediterranean diet</li>
                  <li>• Exercise program</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            This case demonstrates personalized nutraceutical selection based on specific biomarker abnormalities, with clear monitoring and adjustment protocols.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 31,
      title: "Summary & Key Takeaways",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Summary & Key Takeaways</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-700">Longevity medicine is proactive, personalized, and systems-based.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-green-700">Supplements/nutraceuticals can target aging hallmarks, but require evidence-based, individualized protocols.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-purple-700">Clinical application: test, interpret, personalize, monitor, adjust.</p>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-orange-700">Most robust evidence: Omega-3s, Vitamin D, berberine for metabolic/CVD; emerging: NAD+ precursors, senolytics, spermidine.</p>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-yellow-700">Always consider safety, drug interactions, and patient context.</p>
            </div>
          </div>
          
          <KeyTakeaway>
            The future of longevity medicine lies in precision nutraceutical protocols guided by biomarkers, genetics, and individual aging patterns rather than one-size-fits-all approaches.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 32,
      title: "References",
      content: (
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">References</h2>
          
          <p className="text-lg text-gray-600 mb-6 italic">(Abbreviated for slide; full list in handout)</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <ol className="space-y-2 text-sm text-gray-700">
                <li>1. Physicians Assistance: Longevity Medicine, 2025</li>
                <li>2. DCIMedicine: Longevity Medicine, 2025</li>
                <li>3. PMC9654660</li>
                <li>4. Vantage Nutrition, 2025</li>
                <li>5. DO-HEALTH, PubMed 39900648</li>
                <li>6. PMC7963035</li>
                <li>7. PMC8612620</li>
                <li>13. PubMed 40117359</li>
                <li>17. medRxiv 2025.03.19.25324259</li>
              </ol>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-700 font-medium">...</p>
              <p className="text-blue-700 mt-2">[See handout for full reference list]</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 33,
      title: "Q&A",
      content: (
        <div className="text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">Thank you!</h2>
          
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-lg border border-blue-200">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">Questions?</h3>
            <p className="text-lg text-gray-600">[Contact info]</p>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <p className="text-gray-700">
              For additional resources and detailed protocols, please refer to the comprehensive handout materials.
            </p>
          </div>
        </div>
      )
    }
  ];

  // Combine initial slides with remaining slides
  const allSlides = [...slides, ...remainingSlides];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % allSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + allSlides.length) % allSlides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
      <SignedIn>
        <div className={`min-h-screen bg-white ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
          {!isFullscreen && (
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Link href="/" className="flex items-center hover:text-blue-600 transition-colors">
                  <Home className="w-4 h-4 mr-1" />
                  Home
                </Link>
                <span>/</span>
                <Link href="/mentorship" className="flex items-center hover:text-blue-600 transition-colors">
                  <Book className="w-4 h-4 mr-1" />
                  Mentorship
                </Link>
                <span>/</span>
                <span className="text-gray-900 font-medium">Supplements and Nutraceuticals</span>
              </div>
            </div>
          )}

          <div className="max-w-6xl mx-auto px-6 py-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Slideshow Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h1 className="text-2xl font-bold">Supplements and Nutraceuticals in Longevity Medicine</h1>
                    <p className="text-blue-100 mt-2">Slide {currentSlide + 1} of {allSlides.length}</p>
                  </div>
                  <button
                    onClick={toggleFullscreen}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-lg transition-colors"
                  >
                    <Maximize2 className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4 bg-white bg-opacity-20 rounded-full h-2">
                  <div 
                    className="bg-white h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentSlide + 1) / allSlides.length) * 100}%` }}
                  />
                </div>
              </div>

              {/* Slide Content */}
              <div className="p-8 min-h-[600px]">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{allSlides[currentSlide].title}</h2>
                  {allSlides[currentSlide].content}
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                {/* Slide Dots */}
                <div className="flex space-x-2 max-w-md overflow-x-auto">
                  {allSlides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  disabled={currentSlide === allSlides.length - 1}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Accompanying Audio Embed */}
          <div className="max-w-6xl mx-auto px-6 pb-8">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Accompanying Audio</h3>
              <div className="flex justify-center">
                <iframe
                  src="https://share.descript.com/embed/2cdi3V2DUvy"
                  width="640"
                  height="360"
                  frameBorder="0"
                  allowFullScreen
                  className="rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>
      </SignedIn>
    </>
  );
}